import classNames from "classnames";
import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useBreakpoints,
  useDesktopBreakpoint,
  useDialog,
  useFeatureFlagProviders,
  useIsMobile,
  useIsPortalDisabled,
  useLanguage,
  useLocalizedText,
  useNotifications,
  useProgressIndicatorOverlay,
  useProgressIndicatorOverlayLoading,
  useSetBreakpoints,
  useSetDesktopBreakpoint,
  useSetLanguage,
  useSetTenant,
  useSetUser,
  useTenant,
  useTextTemplatesByLanguage,
  useUser,
} from "../../hooks";
import {
  evalFeatureFlagQuery,
  evalShowHideConditions,
  NonUIShowHideCondition,
  ShowHideCondition,
} from "../../utils/common-utils";
import { Breadcrumbs } from "../breadcrumbs/breadcrumbs";
import { GlobalHeader } from "../global-header/global-header";
import {
  AppBreadcrumbsOptions,
  AppBreadcrumbsState,
  AppContextValue,
  AppHeaderOptions,
  AppHeaderState,
  FeatureFlagProviderName,
  FeatureFlagQuery,
  TextTemplateKey,
  User,
} from "../types";
import { AppProps } from "./app";
import { AppContext } from "./app-context";

export interface AppLayoutProps<
  T extends TextTemplateKey = TextTemplateKey,
  C extends object = object,
  F extends FeatureFlagProviderName = FeatureFlagProviderName
> {
  appProps: AppProps<T, C, F>;
  children?: ReactNode | undefined;
}

export function AppLayout<
  T extends TextTemplateKey = TextTemplateKey,
  C extends object = object,
  F extends FeatureFlagProviderName = FeatureFlagProviderName
>({ appProps, children }: AppLayoutProps<T, C, F>) {
  const isMobile = useIsMobile();

  const tenant = useTenant(true);
  const getTenant = useCallback(() => tenant, [tenant]);
  const setTenant = useSetTenant();

  const language = useLanguage();
  const getLanguage = useCallback(() => language, [language]);
  const setLanguage = useSetLanguage();
  const textTemplatesByLanguage = useTextTemplatesByLanguage();
  const getTextTemplatesByLanguage = useCallback(() => textTemplatesByLanguage, [textTemplatesByLanguage]);
  const localizedText = useLocalizedText();

  const user = useUser();
  const getUser = useCallback(() => user, [user]);
  const setUserInContextProvider = useSetUser();
  const setUser = useCallback(
    (updatedUser: Partial<User>) => {
      setUserInContextProvider((currentUser) => ({ ...currentUser, ...updatedUser }));
    },
    [setUserInContextProvider]
  );

  const breakpoints = useBreakpoints();
  const getBreakpoints = useCallback(() => breakpoints, [breakpoints]);
  const setBreakpoints = useSetBreakpoints();
  const desktopBreakpoint = useDesktopBreakpoint();
  const getDesktopBreakpoint = useCallback(() => desktopBreakpoint, [desktopBreakpoint]);
  const setDesktopBreakpoint = useSetDesktopBreakpoint();

  const featureFlagProviders = useFeatureFlagProviders();
  const isFeatureFlagEnabled = useCallback(
    (featureFlagQuery: FeatureFlagQuery<F>): boolean => {
      return evalFeatureFlagQuery(featureFlagProviders, featureFlagQuery);
    },
    [featureFlagProviders]
  );

  const portalDisabled = useIsPortalDisabled();
  const isPortalDisabled = useCallback(() => portalDisabled, [portalDisabled]);

  const [custom, setCustomState] = useState(appProps.appSettings?.custom);
  const getCustom = useCallback(() => custom, [custom]);
  const setCustom = useCallback((updatedCustom: Partial<C>) => {
    setCustomState((currentCustomState) => ({ ...currentCustomState, ...updatedCustom }));
  }, []);

  const { openDialog, closeDialog } = useDialog();

  const loading = useProgressIndicatorOverlayLoading();
  const getLoading = useCallback(() => loading, [loading]);
  const { startLoading, endLoading, resetLoading } = useProgressIndicatorOverlay();

  const { showNotification, dismissNotification } = useNotifications();

  const [breadcrumbs, setBreadcrumbs] = useState<AppBreadcrumbsState>({
    show: appProps.appSettings?.showBreadcrumbs || false,
    options: appProps.appSettings?.breadcrumbsOptions || { links: [], currentPageLabel: "" },
  });
  const showBreadcrumbs = useCallback((options: AppBreadcrumbsOptions) => {
    setBreadcrumbs((breadcrumbs) => ({ ...breadcrumbs, show: true, options: { ...breadcrumbs.options, ...options } }));
  }, []);
  const hideBreadcrumbs = useCallback(() => {
    setBreadcrumbs((breadcrumbs) => ({ ...breadcrumbs, show: false }));
  }, []);

  const [header, setHeader] = useState<AppHeaderState>({
    show: appProps.appSettings?.showHeader || false,
    options: appProps.appSettings?.headerOptions || ({} as AppHeaderOptions),
  });
  const handleHeaderNavMenuItemActivate = React.useCallback(
    (activatedId: string) => {
      const updatedNavMenuItems = header.options.navMenuItems.map((item: any) => {
        if (item.subMenuItems?.length > 0) {
          const updatedSubMenuItems = item.subMenuItems.map((subItem: any) => ({
            ...subItem,
            active: subItem.id === activatedId,
          }));
          return { ...item, active: item.id === activatedId, subMenuItems: updatedSubMenuItems };
        } else {
          return { ...item, active: item.id === activatedId };
        }
      });
      const updatedOptions: AppHeaderOptions = {
        navMenuItems: updatedNavMenuItems,
      };
      setHeader((header) => ({ ...header, options: { ...header.options, ...updatedOptions } }));
    },
    [header.options.navMenuItems]
  );
  const showHeader = useCallback((options: AppHeaderOptions) => {
    setHeader((header) => ({ ...header, show: true, options: { ...header.options, ...options } }));
  }, []);
  const getHeaderOptions = useCallback(() => header.options, [header.options]);
  const setHeaderOptions = useCallback((options: AppHeaderOptions) => {
    setHeader((header) => ({ ...header, options: { ...header.options, ...options } }));
  }, []);
  const hideHeader = useCallback(() => {
    setHeader((header) => ({ ...header, show: false }));
  }, []);

  const evalShowHide = useCallback(
    (showHideConditions: ShowHideCondition<F> | NonUIShowHideCondition<F>[]) => {
      return evalShowHideConditions(
        user.isSignedIn,
        user.permissions,
        user.roles,
        tenant,
        breakpoints,
        desktopBreakpoint,
        featureFlagProviders,
        Array.isArray(showHideConditions) ? showHideConditions : [showHideConditions]
      );
    },
    [breakpoints, desktopBreakpoint, featureFlagProviders, tenant, user.isSignedIn, user.permissions, user.roles]
  );

  const contextValue = useMemo<AppContextValue<T, C, F>>(
    () => ({
      appSettings: {
        getLanguage,
        setLanguage,
        getTextTemplatesByLanguage,
        localizedText,
        getCustom,
        setCustom,
        getTenant,
        setTenant,
        getUser,
        setUser,
        getBreakpoints,
        setBreakpoints,
        getDesktopBreakpoint,
        setDesktopBreakpoint,
        isFeatureFlagEnabled,
        isPortalDisabled,
      },
      appUI: {
        openAppDialog: openDialog,
        closeAppDialog: closeDialog,
        showBreadcrumbs,
        hideBreadcrumbs,
        getLoading,
        startLoading,
        endLoading,
        resetLoading,
        showNotification,
        removeNotification: dismissNotification,
        showHeader,
        hideHeader,
        getHeaderOptions,
        setHeaderOptions,
        evalShowHide,
        isMobile:
          appProps.appSettings?.forceScreenSize === undefined
            ? isMobile
            : appProps.appSettings.forceScreenSize === "mobile",
        isDesktop:
          appProps.appSettings?.forceScreenSize === undefined
            ? !isMobile
            : appProps.appSettings.forceScreenSize === "desktop",
      },
    }),
    [
      getLanguage,
      setLanguage,
      getTextTemplatesByLanguage,
      localizedText,
      getCustom,
      setCustom,
      getTenant,
      setTenant,
      getUser,
      setUser,
      getBreakpoints,
      setBreakpoints,
      getDesktopBreakpoint,
      setDesktopBreakpoint,
      isFeatureFlagEnabled,
      isPortalDisabled,
      openDialog,
      closeDialog,
      showBreadcrumbs,
      hideBreadcrumbs,
      getLoading,
      startLoading,
      endLoading,
      resetLoading,
      showNotification,
      dismissNotification,
      showHeader,
      hideHeader,
      getHeaderOptions,
      setHeaderOptions,
      evalShowHide,
      appProps.appSettings?.forceScreenSize,
      isMobile,
    ]
  );

  /**
   * Logic to calculate app top padding to be applied if we have a sticky header
   */
  const globalHeaderRef = useRef<HTMLElement>();
  const [appPaddingTop, setAppPaddingTop] = useState(0);
  useEffect(() => {
    if (header.options.sticky) {
      setAppPaddingTop(globalHeaderRef.current?.getBoundingClientRect().height ?? 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [header.options.sticky, globalHeaderRef.current]);

  return (
    <AppContext.Provider value={contextValue}>
      <div className={appProps.className} style={{ paddingTop: appPaddingTop, ...appProps.style }}>
        {header.show && (
          <GlobalHeader
            innerRef={globalHeaderRef}
            {...header.options}
            onNavMenuItemActivate={handleHeaderNavMenuItemActivate}
          />
        )}

        {breadcrumbs.show && (
          <Breadcrumbs
            {...breadcrumbs.options}
            className={classNames(
              "px-6 py-3",
              { "container mx-auto": header.options.constrainContentWidth },
              breadcrumbs.options.className
            )}
          />
        )}

        {children}
      </div>
    </AppContext.Provider>
  );
}
